import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { existsSync, readFileSync } from 'fs';
import { join } from 'path';
import { map, Observable, of, retry, timeout, TimeoutError, timer } from 'rxjs';
import { Constant } from 'src/constant';
import { makeStateKey, TransferState } from '@angular/core';
import { environment } from 'src/environments/environment';

function handleRelativeLinks(data) {
    const targetHostname = environment.appDomainWithProtocol.replace('//', '\/\/');
    return JSON.parse(
        JSON.stringify(data)
            .replace(/:\"\//gm, `: \"${targetHostname}/`)
            .replace(/href=\\"\//gm, `href=\\\"${targetHostname}/`));
}

/**
 * Retry policy for the browser translation-file fetch. Only the HTTP fallback branch of
 * {@link TranslateBrowserLoader} is retried; a transient connection loss (status 0 /
 * {@link TimeoutError}) self-heals when the network returns, instead of leaving the language
 * switch as a silent no-op.
 */
export interface TranslationLoadRetryConfig {
    /** Number of RETRIES after the initial attempt. */
    maxRetries: number;
    /** Backoff before retry N (1-based), in ms. */
    delayMs: (retryCount: number) => number;
    /** Per-attempt cap — HttpClient has no default timeout, so a hung socket would otherwise freeze the switch. */
    perAttemptTimeoutMs: number;
    /** Overall cap across all attempts + backoff, so a pending navigation can never wait indefinitely. */
    overallTimeoutMs: number;
}

export const DEFAULT_TRANSLATION_RETRY: TranslationLoadRetryConfig = {
    maxRetries: 2, // 3 attempts total
    delayMs: (retryCount) => (retryCount <= 1 ? 1_000 : 2_000),
    perAttemptTimeoutMs: 5_000,
    // Safety ceiling, NOT the primary cap: it must exceed the worst-case retry budget
    // (3 * 5_000 attempts + 1_000 + 2_000 backoffs = 18_000 ms) so it never guillotines a legitimate
    // in-progress retry - otherwise the last configured attempt would be unreachable. A hard offline
    // still fails fast (~3 s, status 0 returns instantly); this ceiling only bounds a hung-socket
    // pathology. Keep it >= the worst case whenever the retry params change (see the M4 invariant test).
    overallTimeoutMs: 20_000,
};

/**
 * A translation fetch is worth retrying only when it failed for a transport reason: a status-0
 * `HttpErrorResponse` (offline / ERR_FAILED / CORS) or a per-attempt {@link TimeoutError}. A real
 * HTTP status (e.g. 404 from a bad deploy) is NOT retried — fail fast to the offline modal.
 */
function isRetriableTranslationError(error: unknown): boolean {
    return error instanceof TimeoutError
        || (typeof error === 'object' && error !== null && (error as { status?: number }).status === 0);
}

export class TranslateBrowserLoader implements TranslateLoader {
    constructor(
        private transferState: TransferState,
        private http: HttpClient,
        private prefix: string = './assets/i18n/',
        private suffix: string = '.json',
        private retryConfig: TranslationLoadRetryConfig = DEFAULT_TRANSLATION_RETRY,
    ) { }

    public getTranslation(lang: string): Observable<any> {
        const key = makeStateKey<any>('transfer-translate-' + lang);
        const data = this.transferState.get(key, null);
        // First we are looking for the translations in transfer-state, if none found, http load as fallback
        if (data) {
            return of(handleRelativeLinks(data));
        } else {
            const { maxRetries, delayMs, perAttemptTimeoutMs, overallTimeoutMs } = this.retryConfig;
            return new TranslateHttpLoader(this.http, this.prefix, this.suffix).getTranslation(lang).pipe(
                // Per-attempt cap: a hung GET becomes a TimeoutError -> retriable, instead of freezing.
                timeout(perAttemptTimeoutMs),
                retry({
                    count: maxRetries,
                    delay: (error, retryCount) => {
                        if (isRetriableTranslationError(error)) {
                            return timer(delayMs(retryCount));
                        }
                        throw error; // non-transport error: stop retrying, propagate
                    },
                }),
                // Overall cap across attempts + backoff, so a pending navigation cannot wait forever.
                timeout(overallTimeoutMs),
                map(data => handleRelativeLinks(data)),
            );
        }
    }
}

export class TranslateFSLoader implements TranslateLoader {
    constructor(
        private transferState: TransferState,
        private prefix = './assets/i18n/',
        private suffix = '.json'
    ) { }

    /**
     * Gets the translations from the server, store them in the transfer state
     */
    public getTranslation(lang: string): Observable<any> {
        const path = join(__dirname, '../browser', this.prefix, `${lang}${this.suffix}`);
        if (existsSync(path)) {
            const key = makeStateKey<any>('transfer-translate-' + lang);
            const data = JSON.parse(readFileSync(path, 'utf8'));
            const processed = handleRelativeLinks(data);
            this.transferState.set(key, processed);
            return of(processed);
        } else {
            const fallbackPath = join(__dirname, '../browser', this.prefix, `${Constant.languageFallback}${this.suffix}`);
            const key = makeStateKey<any>('transfer-translate-' + lang);
            const data = JSON.parse(readFileSync(fallbackPath, 'utf8'));
            const processed = handleRelativeLinks(data);
            this.transferState.set(key, processed);
            return of(processed);
        }
    }
}

export function translateLoaderFactory(httpClient: HttpClient, transferState: TransferState, platform: any) {
    const prefix = './assets/i18n/';
    return isPlatformBrowser(platform)
        ? new TranslateBrowserLoader(transferState, httpClient, prefix)
        : new TranslateFSLoader(transferState, prefix);
}
